import {
  Alert,
  Badge,
  Button,
  Card,
  Checkbox,
  Col,
  Collapse,
  Form,
  Input,
  message,
  Modal,
  Row,
  Select,
  Space,
  Spin,
  Typography,
} from "antd";
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Event, StudyProgram, StudyProgramQuota } from "@bindeleddet/core/types";
import dayjs from "dayjs";

const { Title } = Typography;
const { Option } = Select;
const { Panel } = Collapse;
const { TextArea } = Input;

interface FormValues {
  title: string;
  body: string;
  eventId?: number;
  topics: string[];
}

interface TopicSelection {
  [studyProgramId: number]: number[]; // Map of study program ID to array of selected year levels
}

// Map of study program ID to Set of allowed year levels
type AllowedYearLevels = Map<number, Set<number>>;

const SendPushNotification = () => {
  const [form] = Form.useForm();
  const [allStudyPrograms, setAllStudyPrograms] = useState<StudyProgram[]>([]);
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [previewing, setPreviewing] = useState<boolean>(false);
  const [selectedTopics, setSelectedTopics] = useState<TopicSelection>({});
  const [previewData, setPreviewData] = useState<{
    eligible_user_count: number;
    excluded_count: number;
    final_user_count: number;
    token_count: number;
    users_with_tokens_count: number;
  } | null>(null);
  const [eventQuotas, setEventQuotas] = useState<StudyProgramQuota[]>([]);
  const [selectedEventId, setSelectedEventId] = useState<number | undefined>(undefined);
  const [quotasLoading, setQuotasLoading] = useState<boolean>(false);
  const [showConfirmModal, setShowConfirmModal] = useState<boolean>(false);
  const [pendingFormValues, setPendingFormValues] = useState<FormValues | null>(null);

  // Fetch study programs
  useEffect(() => {
    axios
      .get<StudyProgram[]>("/students/study-program/")
      .then(response => {
        // Filter out "Alle" program and sort (put indok first if exists)
        const filteredPrograms = response.data.filter(program => program.name !== "Alle");
        const indokIndex = filteredPrograms.findIndex(obj => obj.id === 1);
        if (indokIndex !== -1) {
          const indok = filteredPrograms.splice(indokIndex, 1)[0];
          filteredPrograms.unshift(indok);
        }
        setAllStudyPrograms(filteredPrograms);
      })
      .catch(() => {
        message.error("Kunne ikke hente studieprogrammer");
      });
  }, []);

  // Fetch future events
  useEffect(() => {
    setLoading(true);
    axios
      .get<Event[]>("/events/future-events/")
      .then(response => {
        setEvents(response.data);
        setLoading(false);
      })
      .catch(() => {
        message.error("Kunne ikke hente arrangementer");
        setLoading(false);
      });
  }, []);

  // Fetch event quotas when event is selected
  useEffect(() => {
    if (selectedEventId) {
      setQuotasLoading(true);
      axios
        .get<StudyProgramQuota[]>(`/events/${selectedEventId}/study-program-quotas/`)
        .then(response => {
          const quotaMap = new Map<string, StudyProgramQuota>();

          response.data.forEach(quota => {
            const key = (quota.study_programme as unknown) as string;
            const existing = quotaMap.get(key);

            if (existing) {
              existing.year_levels_accepted = Array.from(
                new Set([...existing.year_levels_accepted, ...quota.year_levels_accepted])
              );
            } else {
              quotaMap.set(key, {
                ...quota,
                year_levels_accepted: [...quota.year_levels_accepted],
              });
            }
          });

          const mergedQuotas: StudyProgramQuota[] = Array.from(quotaMap.values());
          setEventQuotas(mergedQuotas);
          setQuotasLoading(false);
        })
        .catch(() => {
          message.error("Kunne ikke hente arrangementkvoter");
          setEventQuotas([]);
          setQuotasLoading(false);
        });
    } else {
      setEventQuotas([]);
      setQuotasLoading(false);
    }
  }, [selectedEventId]);

  const allowedYearLevels = useMemo<AllowedYearLevels>(() => {
    const map = new Map<number, Set<number>>();
    if (selectedEventId && eventQuotas.length > 0) {
      // Find "Alle" quota (study_programme ID 0)
      const alleQuota = eventQuotas.find(quota => quota.study_programme === 0);
      const alleYearLevels = alleQuota ? new Set(alleQuota.year_levels_accepted) : null;

      // Process all quotas
      eventQuotas.forEach(quota => {
        // Skip "Alle" quota here, we handle it separately
        if (quota.study_programme === 0) return;

        // Start with "Alle" year levels if they exist, otherwise empty
        const baseYearLevels = alleYearLevels ? new Set(alleYearLevels) : new Set<number>();

        // Merge with specific program quota year levels
        quota.year_levels_accepted.forEach(yearLevel => baseYearLevels.add(yearLevel));

        map.set(quota.study_programme, baseYearLevels);
      });

      // If "Alle" exists, apply it to all programs that don't have specific quotas
      if (alleYearLevels) {
        allStudyPrograms.forEach(program => {
          if (!map.has(program.id)) {
            map.set(program.id, new Set(alleYearLevels));
          }
        });
      }
    }
    return map;
  }, [selectedEventId, eventQuotas, allStudyPrograms]);

  // Check if "Alle" quota exists
  const hasAlleQuota = useMemo(() => {
    return selectedEventId && eventQuotas.some(quota => quota.study_programme === 0);
  }, [selectedEventId, eventQuotas]);

  // Filter study programs based on event selection
  const studyPrograms = useMemo<StudyProgram[]>(() => {
    if (selectedEventId) {
      // If "Alle" quota exists, show all programs
      if (hasAlleQuota) {
        return allStudyPrograms;
      }
      // When event is selected, only show study programs that have quotas (are invited)
      // If eventQuotas is still loading or empty, show empty list (quotas are loading)
      if (allowedYearLevels.size > 0) {
        return allStudyPrograms.filter(program => allowedYearLevels.has(program.id));
      }
      // Event selected but no quotas yet (still loading) or no quotas configured
      return [];
    }
    return allStudyPrograms;
  }, [allStudyPrograms, selectedEventId, allowedYearLevels, hasAlleQuota]);

  // Check if a year level is allowed for a study program
  const isYearLevelAllowed = (studyProgramId: number, yearLevel: number): boolean => {
    if (!selectedEventId) {
      return true; // All year levels allowed when no event is selected
    }
    const allowedLevels = allowedYearLevels.get(studyProgramId);
    return allowedLevels ? allowedLevels.has(yearLevel) : false;
  };

  // Handle year level checkbox change
  const handleYearLevelChange = (studyProgramId: number, yearLevel: number, checked: boolean) => {
    // Prevent selecting disabled year levels
    if (checked && !isYearLevelAllowed(studyProgramId, yearLevel)) {
      return;
    }

    setSelectedTopics(prev => {
      const newSelection = { ...prev };
      if (!newSelection[studyProgramId]) {
        newSelection[studyProgramId] = [];
      }

      if (checked) {
        // Add year level if not already present
        if (!newSelection[studyProgramId].includes(yearLevel)) {
          newSelection[studyProgramId] = [...newSelection[studyProgramId], yearLevel];
        }
      } else {
        // Remove year level
        newSelection[studyProgramId] = newSelection[studyProgramId].filter(yl => yl !== yearLevel);
      }

      // Remove program entry if no year levels selected
      if (newSelection[studyProgramId].length === 0) {
        delete newSelection[studyProgramId];
      }

      return newSelection;
    });
    // Invalidate any existing preview when recipient selection changes
    setPreviewData(null);
  };

  // Clean up selected topics when event changes (remove disallowed combinations)
  useEffect(() => {
    if (!selectedEventId) {
      return;
    }

    // If the selected event has no allowed year levels (no quotas), clear all selections
    if (allowedYearLevels.size === 0) {
      setSelectedTopics({});
      return;
    }

    setSelectedTopics(prev => {
      const cleaned: TopicSelection = {};
      Object.keys(prev).forEach(programIdStr => {
        const programId = parseInt(programIdStr, 10);
        const allowedLevels = allowedYearLevels.get(programId);
        // Only keep programs that are invited and only keep allowed year levels
        if (allowedLevels) {
          const filtered = prev[programId].filter(yl => allowedLevels.has(yl));
          if (filtered.length > 0) {
            cleaned[programId] = filtered;
          }
        }
      });
      return cleaned;
    });
  }, [selectedEventId, allowedYearLevels]);

  // Generate recipient groups from selected topics (format: "shortname-year_level")
  const generateRecipientGroups = (): string[] => {
    const groups: string[] = [];
    Object.keys(selectedTopics).forEach(programIdStr => {
      const programId = parseInt(programIdStr, 10);
      // Use allStudyPrograms as fallback in case program is not in filtered studyPrograms
      const program = studyPrograms.find(p => p.id === programId) || allStudyPrograms.find(p => p.id === programId);
      if (program) {
        selectedTopics[programId].forEach(yearLevel => {
          groups.push(`${program.shortname.toLowerCase()}-${yearLevel}`);
        });
      }
    });
    return groups;
  };

  // Handle preview
  const handlePreview = async () => {
    const recipientGroups = generateRecipientGroups();

    if (recipientGroups.length === 0) {
      message.error("Du må velge minst ett emne (studieprogram + årstrinn)");
      return;
    }

    setPreviewing(true);
    setPreviewData(null);

    try {
      const values = form.getFieldsValue();
      const hasEvent = !!values.eventId;

      const payload = {
        recipient_groups: recipientGroups,
        ...(hasEvent ? { event_id: values.eventId } : {}),
        exclude_registered: hasEvent,
      };

      const response = await axios.post("/push-notifications/preview/", payload);

      if (response.status === 200 && response.data) {
        setPreviewData(response.data);
      } else {
        throw new Error("Uventet respons fra server");
      }
    } catch (error: unknown) {
      let errorMessage = "Ukjent feil";
      if (axios.isAxiosError(error)) {
        errorMessage = error.response?.data?.error || error.message || "Kunne ikke hente forhåndsvisning";
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }
      message.error(`Kunne ikke hente forhåndsvisning: ${errorMessage}`);
    } finally {
      setPreviewing(false);
    }
  };

  // Actually send the push notification
  const sendPushNotification = async (values: FormValues) => {
    const recipientGroups = generateRecipientGroups();

    if (recipientGroups.length === 0) {
      message.error("Du må velge minst ett emne (studieprogram + årstrinn)");
      return;
    }

    setSubmitting(true);

    try {
      // Build payload for backend API
      // If event is selected, exclude registered/waitlist students. Otherwise, don't exclude anyone.
      const hasEvent = !!values.eventId;
      const payload = {
        title: values.title || "Standard tittel",
        body: values.body,
        recipient_groups: recipientGroups,
        ...(hasEvent ? { event_id: values.eventId } : {}),
        exclude_registered: hasEvent, // Only exclude if event is selected
        ...(hasEvent ? { data_payload: { type: "event", id: String(values.eventId) } } : {}),
      };

      // Call backend API endpoint
      const response = await axios.post("/push-notifications/send/", payload);

      if (response.status === 200 && response.data) {
        const result = response.data;

        // Check if anything was actually sent
        if (result.error_message) {
          message.error(`Feil: ${result.error_message}`);
        } else if (result.token_count === 0) {
          if (result.eligible_user_count === 0) {
            message.warning("Ingen studenter matchet kriteriene. Ingen push-notifikasjoner ble sendt.");
          } else {
            message.warning(
              `${result.eligible_user_count} studenter matchet, men ingen har registrert push-notifikasjoner. ` +
                "Ingen varsler ble sendt."
            );
          }
        } else if (result.success_count === 0) {
          // Check if all tokens were invalid
          if (result.invalid_tokens_count && result.invalid_tokens_count === result.token_count) {
            message.warning(
              `Alle ${result.token_count} enheter har ugyldige push-notifikasjon tokens. ` +
                "Tokenene er automatisk deaktivert. Ingen varsler ble sendt."
            );
          } else if (result.invalid_tokens_count && result.invalid_tokens_count > 0) {
            message.warning(
              `Fant ${result.token_count} enheter, men ingen varsler ble sendt. ` +
                `${result.invalid_tokens_count} token(er) var ugyldige og er deaktivert. ` +
                "Sjekk Firebase-konfigurasjon eller se feilmeldinger."
            );
          } else {
            message.warning(
              `Fant ${result.token_count} enheter, men ingen varsler ble sendt. ` +
                "Sjekk Firebase-konfigurasjon eller se feilmeldinger."
            );
          }
        } else {
          const successMessage = `Push-notifikasjon sendt! ${result.success_count} sendt til ${result.token_count} enheter`;
          message.success(successMessage);

          form.resetFields();
          setSelectedTopics({});
          setPreviewData(null);
          setSelectedEventId(undefined);
        }

        if (result.excluded_count > 0) {
          message.info(`${result.excluded_count} studenter ekskludert (allerede registrert eller på venteliste)`);
        }

        if (result.failure_count > 0 && result.success_count > 0) {
          // Only show failure count if there were some successes (partial failure)
          message.warning(`${result.failure_count} feilet`);
        }

        if (result.invalid_tokens_count && result.invalid_tokens_count > 0 && result.success_count > 0) {
          message.info(`${result.invalid_tokens_count} ugyldig(e) token(er) deaktivert`);
        }
      } else {
        throw new Error("Uventet respons fra server");
      }
    } catch (error: unknown) {
      let errorMessage = "Ukjent feil";
      if (axios.isAxiosError(error)) {
        errorMessage = error.response?.data?.error || error.message || "Kunne ikke sende push-notifikasjon";
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }
      message.error(`Kunne ikke sende push-notifikasjon: ${errorMessage}`);
    } finally {
      setSubmitting(false);
      setShowConfirmModal(false);
      setPendingFormValues(null);
    }
  };

  // Handle form submission - show confirmation modal
  const onFinish = async (values: FormValues) => {
    const recipientGroups = generateRecipientGroups();

    if (recipientGroups.length === 0) {
      message.error("Du må velge minst ett emne (studieprogram + årstrinn)");
      return;
    }

    setPendingFormValues(values);

    // Fetch preview data if not already available, then show modal
    if (!previewData) {
      setPreviewing(true);
      try {
        const hasEvent = !!values.eventId;
        const payload = {
          recipient_groups: recipientGroups,
          ...(hasEvent ? { event_id: values.eventId } : {}),
          exclude_registered: hasEvent,
        };

        const response = await axios.post("/push-notifications/preview/", payload);

        if (response.status === 200 && response.data) {
          setPreviewData(response.data);
        }
      } catch (error: unknown) {
        // Don't show error here, just proceed without preview data
        // User will still be able to send
        // Error silently ignored
      } finally {
        setPreviewing(false);
        // Show confirmation modal after preview fetch completes (or fails)
        setShowConfirmModal(true);
      }
    } else {
      // Preview data already available; show confirmation modal immediately
      setShowConfirmModal(true);
    }
  };

  // Handle confirmation modal confirm
  const handleConfirmSend = () => {
    if (pendingFormValues) {
      sendPushNotification(pendingFormValues);
    }
  };

  // Handle confirmation modal cancel
  const handleCancelSend = () => {
    setShowConfirmModal(false);
    setPendingFormValues(null);
  };

  // Format event display text
  const formatEventDisplay = (event: Event): string => {
    const date = dayjs(event.start).format("DD. MMM YYYY, HH:mm");
    return `${event.name} (${date})`;
  };

  // Get selected topics count for display
  const getSelectedTopicsCount = (): number => {
    return Object.values(selectedTopics).reduce((sum, yearLevels) => sum + yearLevels.length, 0);
  };

  // Check if all allowed topics are selected
  const areAllSelected = useMemo(() => {
    if (!selectedEventId || allowedYearLevels.size === 0) return false;

    // Check that there are no selected programs that are NOT in allowedYearLevels
    // (shouldn't happen due to cleanup, but safety check)
    const hasInvalidPrograms = Object.keys(selectedTopics).some(programIdStr => {
      const programId = parseInt(programIdStr, 10);
      return !allowedYearLevels.has(programId);
    });
    if (hasInvalidPrograms) {
      return false; // Found a program that's not allowed
    }

    // Check if all programs in allowedYearLevels are selected
    return Array.from(allowedYearLevels.entries()).every(([programId, allowedLevels]) => {
      const selectedLevels = selectedTopics[programId] || [];

      // Check if all allowed year levels for this program are selected
      if (selectedLevels.length !== allowedLevels.size) return false;

      // Check if all allowed levels are actually selected
      return Array.from(allowedLevels).every(level => selectedLevels.includes(level));
    });
  }, [selectedEventId, allowedYearLevels, selectedTopics]);

  // Get list of selected study programs with year levels for confirmation modal
  // Returns program info with year levels array for displaying with badges
  const getSelectedProgramsList = (): Array<{ id: string; programName: string; yearLevels: number[] }> => {
    const programs: Array<{ id: string; programName: string; yearLevels: number[] }> = [];
    Object.keys(selectedTopics).forEach(programIdStr => {
      const programId = parseInt(programIdStr, 10);
      // Use allStudyPrograms as fallback in case program is not in filtered studyPrograms
      const program = studyPrograms.find(p => p.id === programId) || allStudyPrograms.find(p => p.id === programId);
      if (program) {
        const yearLevels = selectedTopics[programId].sort((a, b) => a - b);
        programs.push({
          id: `${program.id}-${yearLevels.join("-")}`,
          programName: program.name,
          yearLevels,
        });
      }
    });
    return programs;
  };

  // Get notification count for confirmation modal (actual number being sent)
  const getNotificationCount = (): number => {
    if (previewData) {
      // Use token_count (actual notifications being sent)
      return previewData.token_count || 0;
    }
    // No preview data available: do not provide a rough estimate
    return 0;
  };

  // Handle form values change to invalidate preview when event changes
  const handleFormValuesChange = (changedValues: Partial<FormValues>) => {
    if ("eventId" in changedValues) {
      const eventId = changedValues.eventId ?? undefined;
      setSelectedEventId(eventId);
      // Invalidate preview when event selection changes
      setPreviewData(null);
    }
  };

  if (loading) {
    return (
      <Card style={{ width: "100%" }}>
        <Spin size="large" style={{ display: "block", textAlign: "center", padding: "2em" }} />
      </Card>
    );
  }

  const selectedProgramsList = getSelectedProgramsList();
  const notificationCount = getNotificationCount();
  const hasPreviewData = !!previewData;

  return (
    <>
      <Modal
        title="Bekreft sending av push-notifikasjon"
        open={showConfirmModal}
        onOk={handleConfirmSend}
        onCancel={handleCancelSend}
        okText="Bekreft"
        cancelText="Avbryt"
        okButtonProps={{ loading: submitting }}
        width={600}
      >
        <div>
          <p style={{ marginBottom: 16, fontSize: 16 }}>
            Du er i ferd med å sende{" "}
            <strong>{hasPreviewData ? notificationCount : `ca. ${notificationCount}`} push-notifikasjoner</strong> til
            disse linjene:
          </p>
          <ul style={{ marginBottom: 16, paddingLeft: 20, listStyle: "none" }}>
            {selectedProgramsList.map(program => (
              <li key={program.id} style={{ marginBottom: 16, display: "flex", alignItems: "center" }}>
                <span style={{ marginRight: 12, minWidth: 120 }}>{program.programName}</span>
                <Row gutter={[8, 0]}>
                  {[1, 2, 3, 4, 5].map(yearLevel => (
                    <Col key={yearLevel} style={{ padding: "0.1em" }}>
                      <Badge
                        count={yearLevel}
                        style={{
                          backgroundColor: program.yearLevels.includes(yearLevel)
                            ? "var(--bindeleddet-color)"
                            : "var(--neutral-alter)",
                          color: program.yearLevels.includes(yearLevel) ? "var(--neutral)" : "var(--neutral-alt)",
                        }}
                      />
                    </Col>
                  ))}
                </Row>
              </li>
            ))}
          </ul>
          {hasPreviewData && previewData && (
            <Alert
              message="Forhåndsvisning"
              description={
                <div>
                  <p>
                    Du har valgt <strong>{previewData.final_user_count}</strong> mottakere
                    {previewData.excluded_count > 0 && <span> ({previewData.excluded_count} ekskludert)</span>}
                  </p>
                  <p>
                    <strong>{previewData.users_with_tokens_count || 0}</strong>{" "}
                    {previewData.users_with_tokens_count === 1 ? "bruker" : "brukere"} har tillatt push-notifikasjoner i
                    appen
                  </p>
                </div>
              }
              type="info"
              showIcon
              style={{ marginTop: 16 }}
            />
          )}
        </div>
      </Modal>

      <Card style={{ width: "100%" }}>
        <Title level={2}>Send push-notifikasjon</Title>

        <Col span={24} style={{ marginBottom: 24 }}>
          <Alert
            message="Push-notifikasjoner sendes til Bindeleddet-appen på iOS og Android"
            description="Push-notifikasjoner vil kun bli mottatt av studenter som har installert og aktivert push-notifikasjoner i Bindeleddet-appen på sin iOS- eller Android-enhet."
            type="info"
            showIcon
          />
        </Col>

        <Form form={form} onFinish={onFinish} onValuesChange={handleFormValuesChange} layout="vertical">
          <Col span={24}>
            <Form.Item
              label="Tittel:"
              name="title"
              rules={[
                {
                  required: true,
                  message: "Skriv inn tittel",
                },
              ]}
            >
              <Input placeholder="F.eks. Bedpres med BCG" />
            </Form.Item>
          </Col>

          <Col span={24}>
            <Form.Item
              label="Melding:"
              name="body"
              rules={[
                {
                  required: true,
                  message: "Skriv inn melding",
                },
                {
                  max: 3000,
                  message: "Meldingen kan ikke være lengre enn 3000 tegn",
                },
              ]}
            >
              <TextArea rows={4} placeholder="F.eks. Trykk her for å lese mer." maxLength={3000} showCount />
            </Form.Item>
          </Col>

          <Col span={24}>
            <Form.Item
              label="Arrangement:"
              name="eventId"
              help={
                selectedEventId
                  ? "Når et arrangement er valgt, vises kun studieprogrammer og årstrinn som er invitert til arrangementet. Push-notifikasjoner vil ikke sendes til studenter som allerede er påmeldt eller på venteliste for arrangementet."
                  : "Hvis du velger et arrangement, vil ikke push-notifikasjoner sendes til studenter som allerede er påmeldt eller på venteliste for arrangementet."
              }
            >
              <Select
                showSearch
                placeholder="Velg arrangement (valgfritt)..."
                allowClear
                filterOption={(input, option) =>
                  (option?.children?.toString().toLowerCase() ?? "").includes(input.toLowerCase())
                }
              >
                {events.map(event => (
                  <Option key={event.id} value={event.id}>
                    {formatEventDisplay(event)}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>

          {selectedEventId && !quotasLoading && eventQuotas.length === 0 && (
            <Col span={24}>
              <Alert
                message="Ingen plassfordeling"
                description="Det er foreløpig ikke satt noen plassfordeling for dette arrangementet."
                type="warning"
                showIcon
                style={{ marginBottom: 16 }}
              />
            </Col>
          )}

          {previewData && (
            <Col span={24}>
              <Alert
                message="Forhåndsvisning"
                description={
                  <div>
                    <p>
                      Du har valgt <strong>{previewData.final_user_count}</strong> mottakere
                      {previewData.excluded_count > 0 && <span> ({previewData.excluded_count} ekskludert)</span>}
                    </p>
                    <p>
                      <strong>{previewData.users_with_tokens_count || 0}</strong>{" "}
                      {previewData.users_with_tokens_count === 1 ? "bruker" : "brukere"} har tillatt push-notifikasjoner
                      i appen
                    </p>
                  </div>
                }
                type="info"
                showIcon
                closable
                onClose={() => setPreviewData(null)}
                style={{ marginBottom: 16 }}
              />
            </Col>
          )}

          <Col span={24} style={{ marginTop: 40 }}>
            <Form.Item
              label="Mottakere:"
              required
              help={getSelectedTopicsCount() > 0 ? `${getSelectedTopicsCount()} emne(r) valgt` : undefined}
            >
              {selectedEventId && allowedYearLevels.size > 0 && (
                <div style={{ marginBottom: 12 }}>
                  {!areAllSelected ? (
                    <Button
                      size="small"
                      onClick={() => {
                        const newSelection: TopicSelection = {};
                        allowedYearLevels.forEach((yearLevels, programId) => {
                          newSelection[programId] = Array.from(yearLevels);
                        });
                        setSelectedTopics(newSelection);
                        setPreviewData(null);
                      }}
                    >
                      Velg alle
                    </Button>
                  ) : (
                    <Button
                      size="small"
                      onClick={() => {
                        setSelectedTopics({});
                        setPreviewData(null);
                      }}
                    >
                      Fjern alle
                    </Button>
                  )}
                </div>
              )}
              <Collapse ghost expandIconPosition="end">
                {studyPrograms.map(program => {
                  const selectedYearLevels = selectedTopics[program.id] || [];
                  const hasSelections = selectedYearLevels.length > 0;

                  return (
                    <Panel
                      key={program.id}
                      header={
                        <span>
                          {program.name}
                          {hasSelections && (
                            <span style={{ marginLeft: "8px", color: "#1890ff" }}>
                              ({selectedYearLevels.length} valgt)
                            </span>
                          )}
                        </span>
                      }
                    >
                      <Row gutter={[16, 8]}>
                        {[1, 2, 3, 4, 5].map(yearLevel => {
                          const isAllowed = isYearLevelAllowed(program.id, yearLevel);
                          const isSelected = selectedYearLevels.includes(yearLevel);
                          return (
                            <Col xs={12} sm={8} md={6} lg={4} key={yearLevel}>
                              <Checkbox
                                checked={isSelected}
                                disabled={!isAllowed}
                                onChange={e => handleYearLevelChange(program.id, yearLevel, e.target.checked)}
                                style={{
                                  opacity: isAllowed ? 1 : 0.4,
                                  cursor: isAllowed ? "pointer" : "not-allowed",
                                }}
                              >
                                <span style={{ color: isAllowed ? undefined : "#bfbfbf" }}>{yearLevel}</span>
                              </Checkbox>
                            </Col>
                          );
                        })}
                      </Row>
                    </Panel>
                  );
                })}
              </Collapse>
            </Form.Item>
          </Col>

          <Col span={24} style={{ textAlign: "right" }}>
            <Form.Item>
              <Space>
                <Button
                  size="large"
                  onClick={handlePreview}
                  loading={previewing}
                  disabled={getSelectedTopicsCount() === 0}
                >
                  Forhåndsvis
                </Button>
                <Button size="large" type="primary" htmlType="submit" loading={submitting}>
                  Send push-notifikasjon
                </Button>
              </Space>
            </Form.Item>
          </Col>
        </Form>
      </Card>
    </>
  );
};

export default SendPushNotification;
